<template>
  <div class="signature-settings">
    <div class="signature-settings__header">
      <h3>Business Signature</h3>
      <p class="signature-settings__subtitle">
        This signature will appear on your invoices and documents.
      </p>
    </div>

    <!-- Error message -->
    <div v-if="error" class="signature-settings__error">
      <span>{{ error }}</span>
      <button @click="error = null" class="signature-settings__error-close">&times;</button>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="signature-settings__loading">
      <div class="intercom-spinner"></div>
    </div>

    <template v-else>
      <!-- Existing signature preview -->
      <div v-if="previewUrl" class="signature-settings__preview">
        <div class="signature-settings__preview-box">
          <img :src="previewUrl" alt="Business Signature" />
        </div>
        <div class="signature-settings__preview-actions">
          <button class="intercom-btn intercom-btn--text" @click="startRedraw">
            Redraw
          </button>
          <button class="intercom-btn intercom-btn--text intercom-btn--danger" @click="handleDelete">
            Remove
          </button>
        </div>
      </div>

      <!-- Drawing canvas -->
      <div v-else class="signature-settings__canvas-section">
        <div class="signature-settings__canvas-wrapper">
          <canvas
            ref="canvasRef"
            class="signature-settings__canvas"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart.prevent="startDrawingTouch"
            @touchmove.prevent="drawTouch"
            @touchend.prevent="stopDrawing"
          />
          <div class="signature-settings__canvas-line" />
          <span class="signature-settings__canvas-hint">Sign above</span>
        </div>
        <div class="signature-settings__canvas-actions">
          <button class="intercom-btn intercom-btn--text" @click="clearCanvas">
            Clear
          </button>
          <button
            class="intercom-btn intercom-btn--primary intercom-btn--sm"
            :disabled="!hasDrawn || uploading"
            @click="handleUpload"
          >
            {{ uploading ? 'Uploading...' : 'Save Signature' }}
          </button>
        </div>
      </div>

      <!-- Settings -->
      <div class="signature-settings__config">
        <div class="signature-settings__toggle-row">
          <div>
            <label class="signature-settings__label">Show on Invoices</label>
            <span class="signature-settings__status" :class="{ active: enabled }">
              {{ enabled ? 'Visible' : 'Hidden' }}
            </span>
          </div>
          <label class="intercom-toggle">
            <input
              type="checkbox"
              v-model="enabled"
              :disabled="!signatureImageKey"
            />
            <span class="intercom-toggle__slider"></span>
          </label>
        </div>

        <div class="signature-settings__field">
          <IntercomInput
            v-model="label"
            label="Signature Label"
            placeholder="e.g. Authorized Signatory"
          />
        </div>

        <div class="signature-settings__save">
          <button
            class="intercom-btn intercom-btn--primary"
            :disabled="saving"
            @click="handleSaveSettings"
          >
            {{ saving ? 'Saving...' : 'Save Settings' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useBilling } from '../../composables/useBilling'
import IntercomInput from '@shared/components/intercom/IntercomInput.vue'

const {
  getBillingSettings,
  updateBillingSettings,
  uploadSignature,
  deleteSignature,
  getSignaturePreview,
} = useBilling()

const loading = ref(true)
const saving = ref(false)
const uploading = ref(false)
const error = ref(null)

const enabled = ref(false)
const label = ref('Authorized Signatory')
const signatureImageKey = ref('')
const previewUrl = ref('')

// Canvas state
const canvasRef = ref(null)
const hasDrawn = ref(false)
let isDrawing = false
let ctx = null

onMounted(async () => {
  await loadSettings()
})

async function loadSettings() {
  loading.value = true
  try {
    const settings = await getBillingSettings()
    enabled.value = settings.signature_enabled || false
    label.value = settings.signature_label || 'Authorized Signatory'
    signatureImageKey.value = settings.signature_image_key || ''

    if (signatureImageKey.value) {
      try {
        const preview = await getSignaturePreview()
        previewUrl.value = preview.preview_url || ''
      } catch {
        previewUrl.value = ''
      }
    }
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'Failed to load settings'
  } finally {
    loading.value = false
    await nextTick()
    if (!previewUrl.value) {
      initCanvas()
    }
  }
}

function initCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return
  canvas.width = canvas.offsetWidth
  canvas.height = 200
  ctx = canvas.getContext('2d')
  ctx.strokeStyle = '#000'
  ctx.lineWidth = 2.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  // White background
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
}

function getPos(e) {
  const rect = canvasRef.value.getBoundingClientRect()
  return { x: e.clientX - rect.left, y: e.clientY - rect.top }
}

function startDrawing(e) {
  isDrawing = true
  hasDrawn.value = true
  const pos = getPos(e)
  ctx.beginPath()
  ctx.moveTo(pos.x, pos.y)
}

function draw(e) {
  if (!isDrawing) return
  const pos = getPos(e)
  ctx.lineTo(pos.x, pos.y)
  ctx.stroke()
}

function stopDrawing() {
  isDrawing = false
}

function startDrawingTouch(e) {
  const touch = e.touches[0]
  startDrawing(touch)
}

function drawTouch(e) {
  const touch = e.touches[0]
  draw(touch)
}

function clearCanvas() {
  hasDrawn.value = false
  if (ctx) {
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  }
}

async function startRedraw() {
  previewUrl.value = ''
  await nextTick()
  initCanvas()
}

async function handleUpload() {
  uploading.value = true
  error.value = null
  try {
    const canvas = canvasRef.value
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
    const file = new File([blob], 'signature.png', { type: 'image/png' })
    const result = await uploadSignature(file)
    signatureImageKey.value = result.image_key || ''
    previewUrl.value = result.preview_url || ''
    enabled.value = true
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'Upload failed'
  } finally {
    uploading.value = false
  }
}

async function handleDelete() {
  saving.value = true
  error.value = null
  try {
    await deleteSignature()
    signatureImageKey.value = ''
    previewUrl.value = ''
    enabled.value = false
    await nextTick()
    initCanvas()
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'Failed to remove'
  } finally {
    saving.value = false
  }
}

async function handleSaveSettings() {
  saving.value = true
  error.value = null
  try {
    await updateBillingSettings({
      signature_enabled: enabled.value,
      signature_label: label.value.trim(),
    })
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'Failed to save'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.signature-settings {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: var(--space-6);
}

.signature-settings__header h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 4px;
}

.signature-settings__subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0;
}

.signature-settings__error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--radius-md);
  color: #dc2626;
  font-size: var(--font-size-sm);
}

.signature-settings__error-close {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
}

.signature-settings__loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.signature-settings__preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-settings__preview-box {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  background: var(--color-white);
  padding: var(--space-4);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.signature-settings__preview-box img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
}

.signature-settings__preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.signature-settings__canvas-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-settings__canvas-wrapper {
  position: relative;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  background: var(--color-white);
  overflow: hidden;
}

.signature-settings__canvas {
  display: block;
  width: 100%;
  height: 200px;
  cursor: crosshair;
}

.signature-settings__canvas-line {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 48px;
  height: 1px;
  background: var(--color-gray-200);
}

.signature-settings__canvas-hint {
  position: absolute;
  left: 24px;
  bottom: 28px;
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.signature-settings__canvas-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.signature-settings__config {
  border-top: 1px solid var(--color-gray-200);
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.signature-settings__toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.signature-settings__label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.signature-settings__status {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: 2px;
}

.signature-settings__status.active {
  color: var(--color-success);
}

.signature-settings__field {
  max-width: 400px;
}

.signature-settings__save {
  display: flex;
  justify-content: flex-end;
}

/* Toggle styles (matching existing patterns in the project) */
.intercom-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.intercom-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.intercom-toggle__slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: var(--color-gray-300);
  border-radius: 24px;
  transition: background var(--transition-fast);
}

.intercom-toggle__slider::before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: var(--color-white);
  border-radius: 50%;
  transition: transform var(--transition-fast);
  box-shadow: var(--shadow-sm);
}

.intercom-toggle input:checked + .intercom-toggle__slider {
  background: var(--color-blue);
}

.intercom-toggle input:checked + .intercom-toggle__slider::before {
  transform: translateX(20px);
}

.intercom-toggle input:disabled + .intercom-toggle__slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Button styles matching project intercom patterns */
.intercom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.intercom-btn--primary {
  background: var(--color-blue);
  color: var(--color-white);
}

.intercom-btn--primary:hover:not(:disabled) {
  background: var(--color-blue-700);
}

.intercom-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.intercom-btn--text {
  background: transparent;
  color: var(--color-blue-600);
  padding: var(--space-1) var(--space-2);
}

.intercom-btn--text:hover {
  text-decoration: underline;
}

.intercom-btn--danger {
  color: var(--color-red-600);
}

.intercom-btn--sm {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-xs);
}
</style>
